/**
 * Image compression utility for bookshelf scanning
 * Compresses images to optimal size for Gemini Vision API while maintaining readability
 */

export interface CompressionStats {
  originalSize: number;
  compressedSize: number;
  reductionPercent: number;
  originalDimensions: { width: number; height: number };
  finalDimensions: { width: number; height: number };
}

export interface CompressionResult {
  blob: Blob;
  base64: string;
  stats: CompressionStats;
}

const DEFAULT_MAX_WIDTH = 1600;
const DEFAULT_MAX_HEIGHT = 1200;
const DEFAULT_QUALITY = 0.85;

/**
 * Compress an image file to optimal size for OCR/vision processing
 */
export async function compressImage(
  file: File | Blob,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
  } = {}
): Promise<CompressionResult> {
  const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH;
  const maxHeight = options.maxHeight || DEFAULT_MAX_HEIGHT;
  const quality = options.quality || DEFAULT_QUALITY;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const originalWidth = width;
        const originalHeight = height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Use better image smoothing for text readability
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            
            // Convert to base64
            const base64Reader = new FileReader();
            base64Reader.onload = () => {
              const base64String = (base64Reader.result as string).split(',')[1];
              
              const originalSize = file instanceof File ? file.size : file.size;
              const compressedSize = blob.size;
              const reductionPercent = Math.round(
                ((originalSize - compressedSize) / originalSize) * 100
              );
              
              resolve({
                blob,
                base64: base64String,
                stats: {
                  originalSize,
                  compressedSize,
                  reductionPercent,
                  originalDimensions: { width: originalWidth, height: originalHeight },
                  finalDimensions: { width, height },
                },
              });
            };
            base64Reader.onerror = () => reject(new Error('Failed to convert to base64'));
            base64Reader.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate image file before compression
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }
  
  // Check file size (max 10MB before compression)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return { valid: false, error: 'Image must be smaller than 10MB' };
  }
  
  return { valid: true };
}
